import React, { useState, useEffect } from 'react';
import { X, CheckCircle, CreditCard, Loader, Shield, ArrowRight, Lock } from 'lucide-react';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe as useStripeElements, useElements } from '@stripe/react-stripe-js';
import { useStripe } from '../../../contexts/StripeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';
import { upgradePlan } from '../../../services/subscriptionService';
import { supabase } from '../../../lib/supabase';

// Inner card payment form using Stripe Elements
const CardPaymentForm = ({ chosenPlan, onSuccess, onError, processing, setProcessing, user }) => {
  const stripe = useStripeElements();
  const elements = useElements();
  const [cardErrors, setCardErrors] = useState({});
  const [cardholderName, setCardholderName] = useState('');

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '15px',
        color: '#1f2937',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': { color: '#9ca3af' },
      },
      invalid: { color: '#ef4444' },
    },
  };

  const handleCardChange = (field) => (event) => {
    setCardErrors(prev => ({ ...prev, [field]: event?.error?.message || '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements || processing) return;
    if (!cardholderName?.trim()) {
      setCardErrors(prev => ({ ...prev, name: 'Cardholder name is required' }));
      return;
    }

    setProcessing(true);
    try {
      const cardNumberElement = elements?.getElement(CardNumberElement);
      const { error, paymentMethod } = await stripe?.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: { name: cardholderName, email: user?.email || '' },
      });

      if (error) {
        setCardErrors({ general: error?.message });
        onError?.(error?.message);
        return;
      }

      // Payment method created — now create a payment intent via edge function
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      let token = supabaseAnonKey || '';
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.access_token) token = session?.access_token;
      } catch (e) {}

      const amountInCents = Math.round((chosenPlan?.amount || 0) * 100);

      const intentResponse = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabaseAnonKey || ''
        },
        body: JSON.stringify({
          planId: chosenPlan?.id,
          amount: amountInCents,
          currency: 'usd',
          paymentMethodId: paymentMethod?.id,
          customerInfo: {
            userId: user?.id || null,
            email: user?.email || '',
            first_name: cardholderName?.split(' ')?.[0] || cardholderName,
            last_name: cardholderName?.split(' ')?.slice(1)?.join(' ') || ''
          },
          metadata: {
            plan_name: chosenPlan?.name || 'Subscription',
            plan_id: chosenPlan?.id || ''
          }
        })
      });

      if (!intentResponse?.ok) {
        const errText = await intentResponse?.text()?.catch(() => 'Payment failed');
        throw new Error(errText);
      }

      const intentData = await intentResponse?.json();
      const clientSecret = intentData?.clientSecret;

      if (!clientSecret) throw new Error('No client secret returned from payment service');

      // Confirm the payment
      const { error: confirmError, paymentIntent } = await stripe?.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod?.id,
      });

      if (confirmError) {
        setCardErrors({ general: confirmError?.message });
        onError?.(confirmError?.message);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess?.(paymentIntent);
      } else {
        throw new Error(`Payment status: ${paymentIntent?.status}. Please try again.`);
      }
    } catch (err) {
      console.error('Card payment error:', err);
      setCardErrors({ general: err?.message || 'Payment failed. Please try again.' });
      onError?.(err?.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {cardErrors?.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{cardErrors?.general}</p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
        <input
          type="text"
          value={cardholderName}
          onChange={e => { setCardholderName(e?.target?.value); setCardErrors(prev => ({ ...prev, name: '' })); }}
          placeholder="John Smith"
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {cardErrors?.name && <p className="text-xs text-red-600 mt-1">{cardErrors?.name}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
        <div className="px-3 py-2.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
          <CardNumberElement options={cardElementOptions} onChange={handleCardChange('cardNumber')} />
        </div>
        {cardErrors?.cardNumber && <p className="text-xs text-red-600 mt-1">{cardErrors?.cardNumber}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
          <div className="px-3 py-2.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            <CardExpiryElement options={cardElementOptions} onChange={handleCardChange('cardExpiry')} />
          </div>
          {cardErrors?.cardExpiry && <p className="text-xs text-red-600 mt-1">{cardErrors?.cardExpiry}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">CVC</label>
          <div className="px-3 py-2.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            <CardCvcElement options={cardElementOptions} onChange={handleCardChange('cardCvc')} />
          </div>
          {cardErrors?.cardCvc && <p className="text-xs text-red-600 mt-1">{cardErrors?.cardCvc}</p>}
        </div>
      </div>
      <button
        type="submit"
        disabled={processing || !stripe}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
      >
        {processing ? (
          <><Loader className="w-4 h-4 animate-spin" /><span>Processing...</span></>
        ) : (
          <><Lock className="w-4 h-4" /><span>Pay ${chosenPlan?.amount}/mo</span></>
        )}
      </button>
      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <Shield className="w-3.5 h-3.5 text-green-500" />
        <span>Secured by Stripe · 256-bit SSL encryption</span>
      </div>
    </form>
  );
};

const PlanUpgradeModal = ({ isOpen, onClose, selectedPlan, currentPlan, availablePlans, onSuccess }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('select'); // 'select' | 'confirm' | 'payment' | 'success'
  const [chosenPlan, setChosenPlan] = useState(null);

  // Get Stripe context safely
  let stripeCtx = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    stripeCtx = useStripe?.();
  } catch {}

  useEffect(() => {
    if (selectedPlan) {
      setChosenPlan(selectedPlan);
      setStep('confirm');
    } else {
      setStep('select');
    }
  }, [selectedPlan, isOpen]);

  if (!isOpen) return null;

  const upgradablePlans = (availablePlans || [])?.filter(p =>
    p?.amount > (currentPlan?.price || 0) || p?.name?.toLowerCase() !== currentPlan?.name?.toLowerCase()
  );

  const formatPrice = (amount) => {
    if (!amount || amount === 0) return 'Free';
    const dollars = amount > 1000 ? Math.round(amount / 100) : Number(amount);
    return `$${dollars}/mo`;
  };

  const handleSelectPlan = (plan) => {
    setChosenPlan(plan);
    setStep('confirm');
  };

  const handleConfirmUpgrade = async () => {
    if (!chosenPlan || !user?.id) return;

    // Free plan — no payment needed
    if (!chosenPlan?.amount || chosenPlan?.amount === 0) {
      setProcessing(true);
      try {
        const result = await upgradePlan(user?.id, chosenPlan?.id);
        if (result?.success) {
          setStep('success');
          toast?.success(`Successfully switched to ${chosenPlan?.name} plan!`, 'Plan Updated ✓', 5000);
          setTimeout(() => { onSuccess?.(); onClose(); }, 2000);
        } else {
          throw new Error(result?.error || 'Failed to update plan');
        }
      } catch (err) {
        toast?.error(err?.message || 'Failed to process upgrade. Please try again.', 'Upgrade Failed');
      } finally {
        setProcessing(false);
      }
      return;
    }

    // Paid plan — try Stripe Checkout Session first (requires stripe_price_id)
    if (stripeCtx?.createCheckoutSession && chosenPlan?.stripe_price_id) {
      setProcessing(true);
      try {
        const result = await stripeCtx?.createCheckoutSession({
          priceId: chosenPlan?.stripe_price_id,
          userId: user?.id,
          planName: chosenPlan?.name,
          amount: chosenPlan?.amount,
          successUrl: `${window.location?.origin}/subscription-management?success=true`,
          cancelUrl: `${window.location?.origin}/subscription-management`,
        });
        if (result?.url) {
          window.location.href = result?.url;
          return;
        }
      } catch (err) {
        console.warn('Checkout session failed, falling back to card form:', err);
      } finally {
        setProcessing(false);
      }
    }

    // No stripe_price_id or checkout session failed — show embedded card form
    setStep('payment');
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Confirm payment on backend (updates subscription + sends email)
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      let token = supabaseAnonKey || '';
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.access_token) token = session?.access_token;
      } catch (e) {}

      await fetch(`${supabaseUrl}/functions/v1/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabaseAnonKey || ''
        },
        body: JSON.stringify({ paymentIntentId: paymentIntent?.id })
      });

      // Also update subscription in DB
      const result = await upgradePlan(user?.id, chosenPlan?.id);
      if (!result?.success) {
        console.warn('DB upgrade after payment failed:', result?.error);
      }
    } catch (err) {
      console.warn('Post-payment processing error (non-fatal):', err);
    }
    setStep('success');
    toast?.success(`Successfully upgraded to ${chosenPlan?.name} plan!`, 'Plan Upgraded ✓', 5000);
    setTimeout(() => { onSuccess?.(); onClose(); }, 2500);
  };

  const handlePaymentError = (errorMsg) => {
    toast?.error(errorMsg || 'Payment failed. Please try again.', 'Payment Failed');
  };

  const stripePromise = stripeCtx?.stripePromise || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'select' ? 'Choose a Plan'
                : step === 'confirm' ? 'Confirm Upgrade'
                : step === 'payment'? 'Enter Payment Details' :'Upgrade Complete!'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {step === 'select' ? 'Select the plan that fits your needs'
                : step === 'confirm' ? `Upgrading to ${chosenPlan?.name}`
                : step === 'payment' ? `${chosenPlan?.name} — ${formatPrice(chosenPlan?.amount)}`
                : 'Your plan has been updated'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Step: Select Plan */}
          {step === 'select' && (
            <div className="space-y-3">
              {upgradablePlans?.map(plan => (
                <button
                  key={plan?.id}
                  onClick={() => handleSelectPlan(plan)}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all group"
                >
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 group-hover:text-blue-700">{plan?.name}</p>
                    <p className="text-sm text-gray-500">{plan?.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-blue-600">{formatPrice(plan?.amount)}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step: Confirm */}
          {step === 'confirm' && chosenPlan && (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-blue-900 text-lg">{chosenPlan?.name} Plan</h3>
                  <span className="text-2xl font-bold text-blue-600">{formatPrice(chosenPlan?.amount)}</span>
                </div>
                <p className="text-sm text-blue-700">{chosenPlan?.description}</p>
                {currentPlan?.name && (
                  <p className="text-xs text-blue-600 mt-2 bg-blue-100 rounded-lg px-3 py-1.5">
                    Upgrading from: <span className="font-semibold">{currentPlan?.name}</span> → <span className="font-semibold">{chosenPlan?.name}</span>
                  </p>
                )}
              </div>

              {chosenPlan?.features && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">What's included:</h4>
                  <ul className="space-y-1.5">
                    {Object.entries(chosenPlan?.features || {})?.slice(0, 6)?.map(([key, val]) => (
                      <li key={key} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{key?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}: {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val === -1 ? 'Unlimited' : val}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Secure payment processing. Cancel anytime.</span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmUpgrade}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-xl font-semibold transition-colors"
                >
                  {processing ? <Loader className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  <span>{processing ? 'Processing...' : `Upgrade to ${chosenPlan?.name}`}</span>
                </button>
              </div>
            </div>
          )}

          {/* Step: Payment — embedded Stripe card form */}
          {step === 'payment' && chosenPlan && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-blue-900">{chosenPlan?.name} Plan</p>
                  <p className="text-xs text-blue-600 mt-0.5">Monthly subscription</p>
                </div>
                <span className="text-xl font-bold text-blue-600">{formatPrice(chosenPlan?.amount)}</span>
              </div>

              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <CardPaymentForm
                    chosenPlan={chosenPlan}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    processing={processing}
                    setProcessing={setProcessing}
                    user={user}
                  />
                </Elements>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CreditCard className="w-6 h-6 text-yellow-600" />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">Payment system is loading...</p>
                  <p className="text-xs text-gray-400">Please wait a moment and try again.</p>
                </div>
              )}

              <button
                onClick={() => setStep('confirm')}
                className="w-full px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                ← Back to Plan Details
              </button>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade Successful!</h3>
              <p className="text-gray-600">You're now on the <span className="font-semibold text-blue-600">{chosenPlan?.name}</span> plan.</p>
              <p className="text-sm text-gray-500 mt-2">A confirmation email has been sent to your inbox.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanUpgradeModal;